const content = `
# Causal Analysis of Multilingual Reasoning

**Research** · University of Munich

## Overview

Large language models trained predominantly on English text nonetheless exhibit strong performance on reasoning tasks posed in other languages. This raises a fundamental question: does a model reason *in* each language separately, or does it translate queries into a shared internal representation — and if so, how causally relevant is that representation to the final answer?

This project applies **causal intervention methods** to study chain-of-thought reasoning pathways in multilingual LLMs, aiming to identify where cross-lingual structure lives in the model and how it influences reasoning outcomes.

## Research Questions

- When a model solves a reasoning problem in French, does it reason in French or does processing shift to a language-agnostic internal space?
- Can causal interventions (e.g. activation patching across languages) identify which layers or components mediate cross-lingual transfer?
- Does the causal structure of reasoning differ between typologically close and distant language pairs?

## Approach

The core tool is **causal tracing** (as in ROME / causal mediation analysis): run a model on a query in language A, then patch activations from a parallel run in language B into various positions and measure the effect on the output. If patching a particular layer's activations from the English run into the German run recovers the English answer, that layer is a causal mediator of cross-lingual generalisation.

This is complemented by **linear probing** across layers to track when language-specific features appear and when they give way to language-agnostic reasoning structure. The combination of probing (where is the information?) and patching (is it causally active?) distinguishes correlation from causal influence.

## Motivation

Multilingual reasoning is typically evaluated behaviourally — does the model get the right answer? Causal analysis opens the internals: it can reveal whether a model that appears to reason multilingually is actually routing all reasoning through a dominant language, which has implications for fairness, reliability, and the interpretability of multilingual systems.
`;
export default content;
